import React, { useState } from 'react';
import { TextInput, DateInput, NumberInput, ReferenceInput, AutocompleteInput, ArrayInput, SimpleFormIterator, NumberField, AddItemButton, useGetIdentity, Pagination, useTranslate, useSimpleFormIterator } from 'react-admin';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import moment from 'moment';
import { useWatch } from 'react-hook-form';

import Create, { CreateActions } from '../common/views/Create';
import SimpleForm from '../common/forms/SimpleForm';
import CustomList from '../common/views/CustomList';
import DateField from '../common//fields/DateField';
import HelpButton from './buttons/HelpButton';
import TimerButton from './buttons/TimerButton';
import CalendarButton from './buttons/CalendarButton';
import TimeEntryRow from './TimeEntryRow';
import ProjectInput from '../projects/inputs/ProjectInput';

const optionText = choice => choice?.name ? choice.name : '';

const formSx = {
    '&& .RaArrayInput-root': {
        minWidth: '100% !important'
    }
};

const simpleFormIteratorSx = {
    '& .RaSimpleFormIterator-line': {
        padding: '15px 0'
    },
    '& .RaSimpleFormIterator-form': {
        gap: '5px'
    },
    '&& .MuiFormControl-root, && .MuiAutocomplete-root': {
        minWidth: '180px !important',
        height: '100%',
        '.MuiInputBase-root': {
            height: '100%'
        },
        '&.ra-input-hours': {
            minWidth: '80px !important',
            width: '80px'
        },
        '&.ra-input-date': {
            minWidth: '140px !important',
            width: '140px'
        },
        '&.ra-input-description': {
            flex: 3,
            minWidth: '275px !important'
        },
        '&.ra-input-notes': {
            minWidth: '200px !important'
        },
        '&.ra-input-project_id': {
            flex: 2,
            '& .MuiFormControl-root': {
                minWidth: '100% !important'
            }
        },
        '& .MuiAutocomplete-inputRoot': {
            paddingRight: '45px',
            '& .MuiAutocomplete-endAdornment': {
                right: '2px',
                '& svg': {
                    fontSize: '1.2rem'
                }
            }
        }
    },
    '& .ra-input-invoiced': {
        justifyContent: 'center',
    }
};

const transform = data => {
    let form = { ...data };
    delete form.default_date;
    return form;
};

const TimeEntryActions = () => (
    <CreateActions>
        <HelpButton />
        <CalendarButton />
        <TimerButton />
    </CreateActions>
);

const AddLineButton = (props) => {
    const { add } = useSimpleFormIterator();

    const handleKeyDown = (e) => {
        if (e.key === "Tab") {
            e.preventDefault();
            add();
        }
    };

    return (
        <AddItemButton onKeyDown={handleKeyDown} {...props} />
    );
};

const TimeEntriesArrayInput = () => {
    const defaultDate = useWatch({ name: "default_date" });

    return (
        <ArrayInput source="time_entries" label="">
            <SimpleFormIterator inline sx={simpleFormIteratorSx} addButton={<AddLineButton />}>
                <DateInput source="date" helperText={false} defaultValue={defaultDate} />
                <ProjectInput helperText={false} />
                <TextInput source="description" multiline rows="2" helperText={false} />
                <ReferenceInput source="type_id" reference="time_entry_types" sort={{ field: 'name', order: 'ASC' }}>
                    <AutocompleteInput optionText={optionText} helperText={false} />
                </ReferenceInput>
                <NumberInput source="hours" label="Hours" step={0.25} min={0.25} helperText={false} />
                <TextInput source="notes" multiline rows="2" helperText={false} />
            </SimpleFormIterator>
        </ArrayInput>
    );
};

const LastTimeEntryList = () => {
    const { data: account } = useGetIdentity();
    const translate = useTranslate();

    return (
        <Grid size={{ xs: 12 }} sx={{ margin: '15px 0' }}>
            <Card>
                <CardHeader title={translate('Last time entries')} />
                <CardContent sx={{ padding: 0 }}>
                    <CustomList
                        resource="time_entries"
                        sort={{ field: 'date', order: 'DESC' }}
                        filter={{ user_id: account?.id }}
                        basePath="/time_entries"
                        filters={null}
                        exporter={false}
                        actions={false}
                        pagination={<Pagination rowsPerPageOptions={[10]} />}
                        perPage={10}
                        empty={false}
                        sx={{ '& .RaList-content': { boxShadow: 'none !important', border: 'none' } }}
                        title=" "
                        disableSyncWithLocation
                    >
                        <TimeEntryRow bulkActionButtons={false} />
                    </CustomList>
                </CardContent>
            </Card>
        </Grid>
    );
};

const TotalField = () => {
    const timeEntries = useWatch({ name: 'time_entries' });
    const totalHours = timeEntries.reduce((acc, timeEntry) => timeEntry?.hours ? acc + timeEntry.hours : acc, 0);
    const hoursByDay = timeEntries.reduce((acc, timeEntry) => {
        if (!timeEntry?.hours || !timeEntry?.date) return acc;
        const existingDay = acc.find(entry => entry.date === timeEntry.date);

        if (existingDay) {
            existingDay.hours += timeEntry.hours;
        } else {
            acc.push({ date: timeEntry.date, hours: timeEntry.hours });
        }

        return acc;
    }, []);

    if (totalHours > 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {hoursByDay.length > 1 && (
                        hoursByDay.map((day, index) => (
                            <Box key={`hours_${index}`} sx={{ display: 'flex', gap: '10px', width: '100%' }}>
                                <DateField source="date" record={day} variant="subtitle2" color="textSecondary" sx={{ flex: 1 }} /> <NumberField source="hours" record={day} options={{ style: 'unit', unit: 'hour' }} variant="subtitle2" color="textSecondary" sx={{ flex: 1, textAlign: 'right' }} />
                            </Box>
                        ))
                    )}
                    <NumberField source="total" record={{ total: totalHours }} options={{ style: 'unit', unit: 'hour' }} variant="subtitle1" color="textSecondary" />
                </Box>
            </Box>
        );
    }

    return null;
};

const TimeEntryBulkCreate = ({ showLastTimeEntryList = true, mutationOptions = {}, ...props }) => {
    const [defaultValues] = useState(props?.defaultValues ? props.defaultValues : { default_date: moment().format('YYYY-MM-DD'), time_entries: [{}] });

    return (
        <>
            <Create actions={<TimeEntryActions />} {...props} resource="time_entries" transform={transform} mutationOptions={{ meta: { bulk: true }, ...mutationOptions }}>
                <SimpleForm defaultValues={defaultValues} sx={formSx}>
                    <DateInput source="default_date" sx={{ margin: 0 }} />
                    <TimeEntriesArrayInput />
                    <TotalField />
                </SimpleForm>
            </Create>
            {showLastTimeEntryList && <LastTimeEntryList />}
        </>
    );
};

export default TimeEntryBulkCreate;
